import { Authority, AuthorityWithConfig } from './authority';
import { EventTypeIdentifier, fleetStages, ObjectToBlur } from '../enums';
import { EnforcementException } from './enforcementException';
import { Time } from './time';
import { ExceptionalPlate } from './whitelisted_plate';

type EventType = EventTypeIdentifier | '*';
type Block = string;
type Translation = string;
export type FleetUUID = string;

export type RouteMappingsArray = [EventType, Block, Translation];

type RouteMapping = Record<EventType, Record<Block, Translation>>;

export interface ImportedEventType {
  id: number;
  identifier: string;
  hidden: boolean;
}

export interface Fleet {
  id: number;
  identifier: string;
  uuid: FleetUUID;
  display_name: string;
  logo_url: string;
  config: FleetConfig;
  whitelist_plates?: ExceptionalPlate[];
  enforcement_exceptions?: EnforcementException[];
  created_by?: string;
  created_at?: Time;
  updated_at?: Time;
  timezone: string;
  stage: fleetStages | null;
  default_ota_group_id?: number | null;
  current_updated_at?: string;
  authorities?: AuthorityWithConfig[];
  permitted_plates_version?: string;
  permitted_plates_updated_at?: string;
  global_route_block_mapping?: RouteMapping;
  region?: string;
  event_types?: ImportedEventType[];
}

export interface CreateFleet extends Partial<Fleet> {
  create_authority?: boolean;
}

export interface UpdateFleet {
  display_name?: string;
  logo_url?: string;
  config?: FleetConfig;
  enforcement_exceptions?: EnforcementException[];
  timezone?: string;
  stage?: fleetStages | null;
  current_updated_at?: string;
  authorities?: Authority[];
  default_ota_group_id?: number | null;
}
export enum NotificationType {
  EMAIL = 'email',
  JIRA = 'jira',
  PAGERDUTY = 'pagerduty',
}

export interface NotificationPolicy {
  type: NotificationType;
  monitoring_period: number;
  cron_schedule: string;
  contact_points: string[] | null;
  type_config?: {
    unknown_status_device_percent_threshold?: number;
    [key: string]: any;
  };
}

export interface DeviceHealthConfig {
  is_enabled: boolean;
  is_bus_status_enabled: boolean;
  is_device_fw_versions_enabled?: boolean;
  is_gtfs_moving_status_enabled: boolean;
  ignore_global_configuration?: boolean;
  check_period: number;
  ota_check_threshold?: number;
  last_check_in_thresholds: Record<string, number> | null;
  synthetic_sections: Record<string, string[]> | null;
  section_mapper: Record<string, string> | null;
  notification_policies: NotificationPolicy[];
}

export interface PortalConfig {
  map?: {
    default_longitude?: number;
    default_latitude?: number;
  };
  customer?: {
    show_only_published_events: boolean;
  };
  rejection_reason_tag_id?: number;
  require_event_type_change_sanity_check?: boolean;
  uses_global_routefile?: boolean;
  hide_offenders_in_spotlight?: boolean;
}

export interface FleetConfig {
  [key: string]: unknown;
  auto_reject_bus_events?: boolean;
  external_vehicle_id_enabled?: boolean;
  match_detector_config?: MatchDetectorConfig;
  portal_config?: PortalConfig;
  device_health_config?: DeviceHealthConfig;
  blurring_service_config?: BlurringServiceConfig;
  monthly_report_generator_config?: MonthlyReportGeneratorConfig | null;
  event_route_updater_config?: EventRouteUpdaterConfig;
  s3_trigger_config?: S3TriggerConfig;
  route_config?: RouteConfig;
  feature_config?: FeatureConfig;
  event_media_sweeper_config?: EventMediaSweeperConfig;
  hawkeye_config?: HawkeyeConfig;
  taskmaster_config?: TaskmasterConfig;
}

export interface FleetWithGroup {
  fleet_uuid: string;
  fleet_name: string;
  group_name: string | null;
  is_primary: boolean;
}

export interface FleetWizardStep {
  fleet: Partial<Fleet>;
  updateFleet: (data: Partial<Fleet>, skipFetch?: boolean) => Promise<Partial<Fleet>>;
  currentStep: number;
  hasPendingConfigChangeRequest: boolean;
  isEditMode: boolean;
}

interface BlurringServiceConfig {
  blur_target_vehicle: boolean;
  blur_videos_only: boolean;
  objects_to_blur: ObjectToBlur;
}

interface RouteConfig {
  version_number?: string;
  version_date?: string;
  is_version_per_event_type?: boolean;
}

interface FeatureConfig {
  use_evidence_images_in_iat: boolean;
  is_license_plate_type_required?: boolean;
  allow_ccr_self_review?: boolean;
}

export interface MatchDetectorConfigItem {
  event_type: EventTypeIdentifier;
  match_type: EventTypeIdentifier;
  grace_period: number;
  reissuance_intval_in_minutes: number;
  min_observation_intval_in_minutes: number;
  max_observation_intval_in_minutes: number;
  consider_only_published_observations: boolean;
  consider_only_vehicles_with_external_id_observations: boolean;
  ignore_match_by_route: boolean;
  ignore_match_by_location: boolean;
  max_target_distance_from_root_in_meters?: number | null;
  min_number_of_sub_observations?: number | null;
  events_can_be_in_multiple_matches_of_different_types: boolean;
}

export interface MatchDetectorConfig {
  is_enabled: boolean;
  violation_detection: {
    configs?: MatchDetectorConfigItem[] | null;
    max_age_in_days?: number | null;
    time_scope?: {
      type: string;
      value: number;
    };
    grace_period?: number;
    reissuance_intval_in_minutes?: number;
    min_observation_intval_in_minutes?: number;
    max_observation_intval_in_minutes?: number;
  };
}

interface MonthlyReportGeneratorConfig {
  [key: string]: unknown;
}

interface EventRouteUpdaterConfig {
  is_enabled: boolean;
}

interface S3TriggerConfig {
  media_requirements: unknown | null;
  auto_review_permitted_list_plate: string;
  auto_reject_target_vehicle_is_bus: boolean;
  forbid_sending_whitelisted_events_to_downstream_services: boolean;
  add_depot_name_to_event: boolean;
  video_duration_limits: Record<string, number> | null;
  blur_event_media: boolean;
  services_that_can_take_blurred_events: string[] | null;
  region_mapper: Record<string, string> | null;
}

interface EventMediaSweeperConfig {
  is_enabled: boolean;
  dry_run: boolean;
  batch_size: number;
  overrides: Record<string, unknown> | null;
}

interface HawkeyeConfig {
  is_enabled: boolean;
  apply_decision: boolean;
  event_types: string[] | null;
  loglevel: string;
  model: {
    weights_path: string;
    device: string;
    classes: number[] | null;
    class_names: string[] | null;
  };
  image_processing: {
    context_width: number;
    context_height: number;
    image_width_after_crop: number;
    image_height_after_crop: number;
    model_img_size: number;
    snapshots_times_relative_to_context: number[] | null;
  };
  thresholds: {
    bus_stop_detection_conf_threshold: number;
    curb_detection_conf_threshold: number;
    flag_offset_from_curve: number;
    decision_threshold_local_space: number;
    decision_threshold_pixel_space: number;
  };
}

interface TaskmasterConfig {
  service_configs: Record<string, unknown> | null;
}

export interface LicensePlateType {
  id?: number;
  license_plate_type_id: number;
  value: string;
  description: string;
}

/**
 * Data structure for importing fleet configuration from JSON file.
 * Based on Fleet interface but with all fields optional for partial imports.
 */
export type ImportedFleetData = Partial<Omit<Fleet, 'id' | 'uuid' | 'created_at' | 'created_by' | 'updated_at'>> & {
  authorities?: Authority[];
  event_types?: ImportedEventType[];
};

export interface FleetValidationResult {
  isValid: boolean;
  errors: string[];
  data?: ImportedFleetData;
}
